import { MaskWithImage } from "@/api/masks-types";
import { RejectWholeEpisodeDialog } from "@/components/episodes/RejectWholeEpisodeDialog";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { EpisodeBreadcrumbs } from "@/components/ui/breadcrumbs/EpisodeBreadcrumbs";
import { Link } from "@/components/ui/Link";
import { BoxLoader } from "@/components/ui/loaders";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { MaskStateChip } from "@/components/ui/state-chips/MaskStateChip";
import { TagsRow } from "@/components/ui/tags/TagsRow";
import { useRejectWholeEpisode } from "@/hooks/use-reject-whole-episode";
import { useEpisodeById } from "@/queries/episodes/use-episode-by-id";
import { useEpisodeMasksByEpisodeId } from "@/queries/episodes/use-episode-masks-by-episode-id";
import {
  isOnlyOperator,
  maskStateOptions,
  submissionStateColumn
} from "@/utils/datagrid-utils";
import { Image } from "@mui/icons-material";
import { Box, Button, Stack } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useParams } from "react-router";

const maskColumns: GridColDef<MaskWithImage>[] = [
  {
    field: "id",
    headerName: "ID",
    width: 120,
    filterable: false,
    renderCell: (params) => (
      <Link to={`/masks/${params.row.id}`}>{params.row.id}</Link>
    ),
  },
  {
    field: "url",
    headerName: "Mask",
    width: 340,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          {params.row.url ? (
            <img
              src={params.row.url}
              alt={`Mask ${params.row.id}`}
              style={{ maxWidth: "300px", margin: "auto" }}
            />
          ) : (
            <Image sx={{ fontSize: "60px", color: grey[400] }} />
          )}
        </Box>
      );
    },
  },
  {
    field: "image.page",
    headerName: "Image Page",
    width: 120,
    filterable: false,
    valueGetter: (_, row) => row.image.page,
  },
  {
    field: "state",
    headerName: "State",
    align: "center",
    headerAlign: "center",
    width: 180,
    filterOperators: isOnlyOperator,
    renderCell: (params) => {
      return <MaskStateChip state={params.row.state} />;
    },
    type: "singleSelect",
    valueOptions: maskStateOptions,
  },
  submissionStateColumn<MaskWithImage>(),
];

export function EpisodeMaskList() {
  const { id } = useParams();

  const { data, isLoading, error } = useEpisodeMasksByEpisodeId(id);
  const { data: episode } = useEpisodeById(id);

  const {
    confirmRejectWholeEpisode,
    setConfirmRejectWholeEpisode,
    handleRejectWholeEpisode,
    isRejectingWholeEpisode,
    canRejectEpisode,
    error: rejectWholeEpisodeError,
  } = useRejectWholeEpisode({
    episodeId: id || "",
    shouldNavigateToNext: false,
  });

  if (!id) {
    return null;
  }

  return (
    <Box sx={{ marginBottom: 2 }}>
      <ApiErrorAlert error={error} />
      <ApiErrorSnackbar error={rejectWholeEpisodeError} />
      <Stack gap={2} mb={2}>
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "33px", // height of Button, set in case no buttons are displayed to have consistent height across different cases
          }}
        >
          {episode && (
            <EpisodeBreadcrumbs
              id={episode.id}
              category={episode.category}
              gender={episode.gender}
              story={episode.story}
              subcategory={episode.subcategory}
            />
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            {canRejectEpisode && (
              <Button
                variant="outlined"
                color="error"
                onClick={() => setConfirmRejectWholeEpisode(true)}
              >
                {"Reject whole story"}
              </Button>
            )}
          </Box>
        </Stack>
        <TagsRow episodeId={id} />
      </Stack>
      <BoxLoader isLoading={isLoading} height={100} />

      <DataGrid
        rows={data?.masks}
        columns={maskColumns}
        columnHeaderHeight={46}
        rowHeight={200}
        rowSelection={false}
        hideFooter={true}
      />
      <RejectWholeEpisodeDialog
        open={confirmRejectWholeEpisode}
        onClose={() => setConfirmRejectWholeEpisode(false)}
        onConfirm={handleRejectWholeEpisode}
        isLoading={isRejectingWholeEpisode}
      />
    </Box>
  );
}
